import { effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs';
import { differenceInMilliseconds } from 'date-fns';
import { Storage } from '@/app/core/services/storage';
import { AuthService, PostAuthLoginRequest, PostAuthRefreshTokenRequest } from '@/app/core/services/auth-service';

export const AUTH_TOKEN_KEY = 'auth-token';

export interface AuthToken {
  account: string;
  accessToken: string;
  expiresAt: string;
  mqttToken: string;
  mqttExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private readonly router = inject(Router);
  private readonly storage = inject(Storage);
  private readonly authService = inject(AuthService);

  private readonly _authToken = signal<AuthToken | null>(
    this.storage.get<AuthToken>(AUTH_TOKEN_KEY, 'object'),
  );

  readonly authToken = this._authToken.asReadonly();

  constructor() {
    effect((onCleanup) => {
      const authToken = this.authToken();

      if (!authToken) {
        return;
      }

      const timeToExpiration = differenceInMilliseconds(new Date(authToken.refreshTokenExpiresAt), new Date());

      if (timeToExpiration <= 0) {
        this.logout();
        return;
      }

      const MAX_TIMEOUT_MS = Math.pow(2, 31) - 1; /* 2147483647 */

      const timerId = setTimeout(() => {
        console.warn('Authentication token expired. Automatically logging out...');
        this.logout();
      }, Math.min(timeToExpiration, MAX_TIMEOUT_MS));

      onCleanup(() => {
        clearTimeout(timerId);
      });
    });
  }

  isAuthenticated(): boolean {
    const token = this.authToken();
    if (!token) return false;

    const isExpired = differenceInMilliseconds(new Date(token.refreshTokenExpiresAt), new Date()) <= 0;
    if (isExpired) {
      this.updateAuthToken(null);
      this.storage.clear(sessionStorage);

      return false;
    }

    return true;
  }

  updateAuthToken(token: AuthToken | null) {
    this._authToken.set(token);
    this.storage.set(AUTH_TOKEN_KEY, token);
  }

  login(request: PostAuthLoginRequest) {
    return this.authService.apiPostAuthLogin(request).pipe(
      tap((res) => {
        const authToken = {
          account: res.data.user.username,
          accessToken: res.data.accessToken,
          expiresAt: new Date(res.data.expiresAt).toISOString(),
          mqttToken: res.data.mqttAccessToken,
          mqttExpiresAt: new Date(res.data.mqttExpiresAt).toISOString(),
          refreshToken: res.data.refreshToken,
          refreshTokenExpiresAt: new Date(res.data.refreshTokenExpiresAt).toISOString(),
        };

        this.updateAuthToken(authToken);
      }),
    );
  }

  logout() {
    return this.authService.apiPostAuthLogout().pipe(
      finalize(() => {
        this.storage.clear(sessionStorage);
        this.updateAuthToken(null);

        void this.router.navigate(['/login']);
      }),
    ).subscribe();
  }

  refreshToken(request: PostAuthRefreshTokenRequest) {
    return this.authService.apiPostAuthRefreshToken(request).pipe(
      tap((res) => {
        const authToken: AuthToken = {
          account: res.data.user.username,
          accessToken: res.data.accessToken,
          expiresAt: new Date(res.data.expiresAt).toISOString(),
          mqttToken: res.data.mqttAccessToken,
          mqttExpiresAt: new Date(res.data.mqttExpiresAt).toISOString(),
          refreshToken: res.data.refreshToken,
          refreshTokenExpiresAt: new Date(res.data.refreshTokenExpiresAt).toISOString(),
        };

        this.updateAuthToken(authToken);
      }),
    );
  }
}
